import { useSelector } from "react-redux"
import RelationField from "../RelationField"
import { useEffect, useState, useMemo } from "react"
import Image from "../Image"
import { coerceRichTextValue } from "../utils/richTextValue"
import FileField from "../FileField"
import EditCalendar from "../editors/EditCalendar"
import { Badge } from "@/components/ui/badge"
import { fetchItemsAPI } from "../api/itemsApi"
import authAPI from "../api/authApi"
import { parseTableTimestamp } from "./componentTable/parseTableTimestamp"
import { getSystemRelationSource } from "../utils/systemRelationSources"
import {
    effectiveImageFieldType,
    formatCellDisplayText,
    resolveConnectorAssetPublicUrl,
    resolveLabwickUploadUrl,
    shouldRenderAsPictureCell,
} from "../utils/pictureUtils"
import { gpxTableLabel, parseGpxValue } from "../utils/gpxValue"
import { GpxMapModal } from "./GpxMapModal"
import {
    relationLabelCache,
    userUidDisplayCache,
    userUidAuthFetchPromises,
    clearRelationLabelCache as clearRelationLabelCacheFromStore
} from "../utils/relationLabelCacheStore"
import { LinkFieldDisplay } from "@/components/LinkFieldDisplay"
import { isLinkFieldType } from "@/utils/linkFieldValue"

/** Strip HTML tags and decode common entities to plain text for safe table display. */
function htmlToPlainText(html: string | null | undefined): string {
    if (html == null) return "";
    const str = String(html);
    const withoutTags = str.replace(/<[^>]+>/g, " ");
    const decoded = withoutTags
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
        .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
    return decoded.replace(/\s+/g, " ").trim();
}

function coerceUid(value: any): string | null {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
        const key = (value as any).uid ?? (value as any).id;
        return key !== undefined && key !== null ? String(key) : null;
    }
    return null;
}

const UUID_LIKE_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

/** Stored relation uids are UUIDs or 24-hex Mongo ObjectIds; human labels (e.g. country names) must not drill in as ids. */
export function looksLikeRelationId(s: string): boolean {
    const t = String(s).trim();
    if (!t) return false;
    return UUID_LIKE_RE.test(t) || MONGO_OBJECT_ID_RE.test(t);
}

function coerceRelationDrillInUid(value: any): string | null {
    const u = coerceUid(value);
    return u && looksLikeRelationId(u) ? u : null;
}

function formatDateOnlyFromIsoLike(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const raw = value.trim();
    if (!raw) return null;
    const dateOnlyMatch = raw.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (dateOnlyMatch) return dateOnlyMatch[1];
    const isoUtcMatch = raw.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/);
    if (!isoUtcMatch) return null;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return isoUtcMatch[1];
    return parsed.toDateString();
}

export function clearRelationLabelCache(): void {
    clearRelationLabelCacheFromStore();
}

type SelectOption = { value: string; label: string };

const parseSelectOptions = (value: any): SelectOption[] => {
    if (!value) return [];

    const toOption = (option: any): SelectOption | null => {
        if (option === null || option === undefined) return null;
        if (typeof option === "string" || typeof option === "number" || typeof option === "boolean") {
            const s = String(option).trim();
            if (!s) return null;
            return { value: s, label: s };
        }
        if (typeof option === "object") {
            const rawValue =
                option.value ??
                option.uid ??
                option.id ??
                option.slug ??
                option.key ??
                option.name ??
                option.title;
            const rawLabel =
                option.label ??
                option.name ??
                option.title ??
                option.value ??
                option.slug ??
                option.uid ??
                option.id;
            const v = rawValue != null ? String(rawValue).trim() : "";
            if (!v) return null;
            const l = rawLabel != null ? String(rawLabel).trim() : v;
            return { value: v, label: l || v };
        }
        return null;
    };

    if (Array.isArray(value)) {
        return value.map(toOption).filter((v): v is SelectOption => Boolean(v));
    }
    if (typeof value === "string") {
        return value
            .split(",")
            .map((option) => option.trim())
            .filter(Boolean)
            .map((s) => ({ value: s, label: s }));
    }
    return [];
};

const getSelectLabel = (value: any, options: SelectOption[]) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    const rawValue = String(value).trim();
    if (!options.length) {
        return rawValue;
    }
    const byValue = options.find((option) => option.value.toLowerCase() === rawValue.toLowerCase());
    if (byValue) return byValue.label;
    const byLabel = options.find((option) => option.label.toLowerCase() === rawValue.toLowerCase());
    return byLabel?.label ?? rawValue;
};

const TableCellTyped = ({
    value,
    meta,
    onImageChange,
    onFileChange,
    onCheckboxClick,
    onNumberAdjust,
    canUpdate = false,
    collection,
    onOpenRelation
}: {
    value: any,
    meta: any,
    onImageChange?: any,
    onFileChange?: (data: { filename: string }) => void,
    onCheckboxClick?: any,
    onNumberAdjust?: (item: any, fieldName: string, delta: number) => void,
    canUpdate?: boolean,
    collection?: string,
    onOpenRelation?: (args: { targetSlug: string; uid: string }) => void
}) => {
    const _type = meta?.type || 'actions'
    const user = useSelector((state: any) => state.user)
    const cellValue = value.getValue()
    const currentType = effectiveImageFieldType(meta, cellValue)
    const [cacheVersion, setCacheVersion] = useState(0)
    const [gpxMapOpen, setGpxMapOpen] = useState(false)

    const relationSlug = meta?.value
    const fieldsToUse = meta?.fields
    const rowOriginal = value?.row?.original as any
    const relationLabelKey = meta?.name ? `${meta.name}__label` : undefined
    const relationLabelsKey = meta?.name ? `${meta.name}__labels` : undefined

    // Stable array instance for read-only relation UIDs to avoid unnecessary
    // re-renders and possible effect loops inside RelationField.
    const relationReadOnlyUids = useMemo(() => {
        const raw = cellValue
        if (raw == null || raw === '') return []
        return Array.isArray(raw) ? raw : [raw]
    }, [cellValue])

    const parseFieldKeys = (fields: any): string[] => {
        if (!fields) return [];
        if (Array.isArray(fields)) return fields.map((f: any) => String(f).trim()).filter(Boolean);
        if (typeof fields === "string") return fields.split(",").map(f => f.trim()).filter(Boolean);
        return [];
    };

    const getNestedValue = (obj: any, path: string): any => {
        if (!obj || typeof obj !== "object") return undefined;
        if (!path.includes(".")) return obj[path];
        return path.split(".").reduce((acc: any, key: string) => (acc && typeof acc === "object" ? acc[key] : undefined), obj);
    };

    const getSystemRelationValue = () => {
        if (meta?.sourceField) {
            return getNestedValue(value.row?.original, meta.sourceField);
        }
        if (relationSlug === 'forms') {
            return getNestedValue(value.row?.original, 'registrationFormUid');
        }
        return cellValue;
    };

    const systemRelationValue = getSystemRelationValue();

    const buildLabelFromFields = (item: any) => {
        const keys = parseFieldKeys(fieldsToUse);
        if (!keys.length) return "";
        const getFlexibleValue = (obj: any, key: string) => {
            const direct = getNestedValue(obj, key);
            if (direct !== undefined) return direct;
            if (!key.includes(".")) {
                const match = Object.keys(obj || {}).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
                if (match) {
                    return obj[match];
                }
            }
            return undefined;
        };
        const parts = keys
            .map((k: string) => getFlexibleValue(item, k))
            .filter((v: any) => v !== undefined && v !== null && String(v).trim() !== "");
        return parts.join(" ").trim();
    };

    useEffect(() => {
        if (currentType !== "multiplerelation" && currentType !== "systemrelation") return;
        if (!relationSlug) return;
        // If backend already provided labels for this cell, skip background label fetching.
        if (rowOriginal && ((relationLabelKey && relationLabelKey in rowOriginal) || (relationLabelsKey && relationLabelsKey in rowOriginal))) {
            return;
        }

        const relationValue = currentType === "systemrelation" ? systemRelationValue : cellValue;
        const items = Array.isArray(relationValue) ? relationValue : relationValue ? [relationValue] : [];
        const ids = items
            .map((item: any) => {
                if (typeof item === "string" || typeof item === "number") return String(item);
                if (item && typeof item === "object") {
                    const key = item.uid ?? item.id;
                    return key !== undefined ? String(key) : null;
                }
                return null;
            })
            .filter((id: any): id is string => Boolean(id));

        if (!ids.length) return;

        if (!relationLabelCache[relationSlug]) {
            relationLabelCache[relationSlug] = new Map<string, string>();
        }
        const cache = relationLabelCache[relationSlug];
        const missing = ids.filter((id: string) => !cache.has(id));
        if (!missing.length) return;

        let cancelled = false;
        const load = async () => {
            try {
                const systemSource = currentType === "systemrelation" ? getSystemRelationSource(relationSlug) : undefined;
                const raw = systemSource
                    ? await systemSource.fetchItems()
                    : (await fetchItemsAPI(relationSlug)).items ?? [];
                const related = Array.isArray(raw) ? raw : [];
                if (cancelled) return;
                related.forEach((record: any) => {
                    const key = record?.uid ?? record?.id;
                    if (!key) return;
                    const label =
                        buildLabelFromFields(record) ||
                        record?.label ||
                        record?.name ||
                        record?.title ||
                        String(key);
                    cache.set(String(key), String(label));
                });
                setCacheVersion((v) => v + 1);
            } catch (err) {
                console.error("TableHelper: failed to load relation labels", err);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [currentType, relationSlug, fieldsToUse, cellValue, systemRelationValue])

    const handleCheckboxClick = (item: any, field: any) => {

        onCheckboxClick(item, field)
    }


    const pictureAssetUrl =
        meta.connectorAssetPublicUrl ||
        resolveConnectorAssetPublicUrl({
            connectorAssetPublicUrl: meta.connectorAssetPublicUrl,
            source: meta.source,
            slug: meta.collectionSlug,
            uid: meta.collectionUid,
        });

    const field = () => {
        const declaredType = String(meta?.type ?? "").trim().toLowerCase()
        if (isLinkFieldType(declaredType)) {
            return (
                <LinkFieldDisplay
                    key={meta.uid}
                    fieldType={declaredType}
                    value={cellValue}
                    className="truncate max-w-full block"
                />
            )
        }
        switch (currentType) {
            case 'text': {
                const raw = value.getValue();
                if (shouldRenderAsPictureCell(meta, raw)) {
                    return (
                        <Image
                            filename={raw}
                            alt={meta.label}
                            parent={value.row.original}
                            connectorAssetPublicUrl={pictureAssetUrl}
                            readOnly
                        />
                    );
                }
                const plain = htmlToPlainText(formatCellDisplayText(raw));
                const normalized = formatDateOnlyFromIsoLike(plain) || plain;
                return <div key={meta.uid} className="truncate max-w-full" title={normalized || undefined}>{normalized || '—'}</div>;
            }
            case 'textarea': {
                const raw = value.getValue();
                if (shouldRenderAsPictureCell(meta, raw)) {
                    return (
                        <Image
                            filename={raw}
                            alt={meta.label}
                            parent={value.row.original}
                            connectorAssetPublicUrl={pictureAssetUrl}
                            readOnly
                        />
                    );
                }
                const plain = htmlToPlainText(formatCellDisplayText(raw));
                const normalized = formatDateOnlyFromIsoLike(plain) || plain;
                return <div key={meta.uid} className="truncate max-w-full" title={normalized || undefined}>{normalized || '—'}</div>;
            }
            case 'relation':
                {
                    const storedKey = coerceUid(cellValue);
                    const drillUid = coerceRelationDrillInUid(cellValue);
                    const labelFromBackend =
                        rowOriginal && relationLabelKey && typeof rowOriginal[relationLabelKey] === 'string'
                            ? String(rowOriginal[relationLabelKey])
                            : '';
                    const rawDisplay = labelFromBackend || (storedKey != null ? String(storedKey) : '');
                    const display = formatDateOnlyFromIsoLike(rawDisplay) || rawDisplay;
                    if (!display) return <span key={meta.uid}></span>;
                    const canDrill = Boolean(drillUid && relationSlug && onOpenRelation);
                    if (!canDrill) {
                        return (
                            <span key={meta.uid} className="truncate max-w-full" title={display}>
                                {display}
                            </span>
                        );
                    }
                    return (
                        <button
                            key={meta.uid}
                            type="button"
                            className="text-left text-gray-900 dark:text-gray-100 hover:underline truncate max-w-full"
                            onClick={(event) => {
                                event.stopPropagation();
                                onOpenRelation!({ targetSlug: String(relationSlug), uid: String(drillUid) });
                            }}
                            title={display}
                        >
                            {display}
                        </button>
                    );
                }
            case "richtext": {
                const plain = htmlToPlainText(coerceRichTextValue(value.getValue()));
                return (
                    <div key={meta.uid} className="truncate max-w-full" title={plain || undefined}>
                        {plain || "—"}
                    </div>
                );
            }
            case "image":
                return (
                    <Image
                        filename={value.getValue()}
                        alt={meta.label}
                        onChange={(img: any) => onImageChange?.(img)}
                        parent={value.row.original}
                        connectorAssetPublicUrl={pictureAssetUrl}
                        allowConnectorUpload={Boolean(pictureAssetUrl && onImageChange)}
                        collectionSlug={collection}
                        fieldName={meta.name}
                    />
                )
            case "file": {
                const fileVal = value.getValue();
                if (onFileChange) {
                    return (
                        <FileField
                            filename={fileVal || ''}
                            label={meta.label}
                            parent={value.row.original}
                            onChange={(data) => onFileChange(data)}
                            collection={collection}
                            fieldName={meta.name}
                        />
                    );
                }
                if (fileVal && fileVal !== '') {
                    const fileUrl = resolveLabwickUploadUrl(fileVal);
                    const displayName = fileVal.split('/').pop() || fileVal;
                    return (
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate max-w-full block">
                            {displayName}
                        </a>
                    );
                }
                return <span className="text-muted-foreground">—</span>;
            }
            case "calendar":
                return (
                    <div className="relative -left-7">
                        <EditCalendar value={value.getValue()} readOnly />
                    </div>
                )
            case "checkbox":
                return (
                    <div className={`rounded-xl w-5 h-5 -top-5 ${value.getValue() === true ? "bg-green-500" : "bg-red-300"}`} onClick={() => { handleCheckboxClick(value.row.original, value) }} />
                )
            case "datetime":
                const dateValue = value.getValue();
                const dateOnlyFromIso = formatDateOnlyFromIsoLike(dateValue);
                if (dateOnlyFromIso) {
                    return <span>{dateOnlyFromIso}</span>;
                }
                const parsedDate = parseTableTimestamp(dateValue);
                if (parsedDate === null) {
                    return <span></span>;
                } else {
                    const isDateOnlyString =
                        typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim());
                    if (isDateOnlyString) {
                        return <span>{parsedDate.toDateString()}</span>;
                    }
                    return <span>{parsedDate.toDateString()} - {parsedDate.toLocaleTimeString('en-GB')}</span>;
                }
            case "date":
            case "dateonly":
                const dateOnlyValue = value.getValue();
                const parsedDateOnly = parseTableTimestamp(dateOnlyValue);
                if (parsedDateOnly === null) {
                    return <span></span>;
                } else {
                    return <span>{parsedDateOnly.toDateString()}</span>;
                }
            case "timeonly":
                const timeOnlyValue = value.getValue();
                const parsedTimeOnly = parseTableTimestamp(timeOnlyValue);
                if (parsedTimeOnly === null) {
                    return <span></span>;
                } else {
                    return <span>{parsedTimeOnly.toLocaleTimeString('en-GB')}</span>;
                }
            case "number":
                const numberValue = value.getValue();
                if (numberValue === null || numberValue === undefined) {
                    return <span></span>;
                }
                if (meta?.inlineAdjust && onNumberAdjust) {
                    const displayValue = typeof numberValue === 'number' ? numberValue.toLocaleString() : numberValue;
                    const fieldName = typeof meta?.name === 'string' && meta.name.trim()
                        ? meta.name.trim()
                        : value?.column?.id;
                    if (!fieldName) {
                        return <div key={meta.uid}>{displayValue}</div>;
                    }
                    return (
                        <div key={meta.uid} className="flex items-center gap-2">
                            <button
                                type="button"
                                className="h-6 w-6 rounded border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                disabled={!canUpdate}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onNumberAdjust(value.row.original, fieldName, -1);
                                }}
                                aria-label="Decrease value"
                            >
                                -
                            </button>
                            <span>{displayValue}</span>
                            <button
                                type="button"
                                className="h-6 w-6 rounded border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                disabled={!canUpdate}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onNumberAdjust(value.row.original, fieldName, 1);
                                }}
                                aria-label="Increase value"
                            >
                                +
                            </button>
                        </div>
                    );
                }
                return <div key={meta.uid}>{typeof numberValue === 'number' ? numberValue.toLocaleString() : numberValue}</div>
            case "select":
            case "dropdown": {
                const options = parseSelectOptions(meta?.options ?? meta?.value);
                const label = getSelectLabel(value.getValue(), options);
                return <span key={meta.uid}>{label}</span>;
            }
            case "multiplerelation": {
                const raw = cellValue;
                const ids = Array.isArray(raw) ? raw : raw ? [raw] : [];
                const backendLabels =
                    rowOriginal && relationLabelsKey && Array.isArray(rowOriginal[relationLabelsKey])
                        ? (rowOriginal[relationLabelsKey] as any[]).map((v) => String(v))
                        : null;
                if (ids.length === 0) {
                    return <span key={meta.uid}></span>;
                }

                const getLabel = (item: any) => {
                    if (item === null || item === undefined) return "";
                    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
                        const cached = relationSlug ? relationLabelCache[relationSlug]?.get(String(item)) : undefined;
                        return cached || String(item);
                    }
                    if (typeof item === "object") {
                        const fromFields = buildLabelFromFields(item);
                        if (fromFields) return fromFields;
                        return (
                            item.label ||
                            item.name ||
                            item.title ||
                            item.uid ||
                            item.id ||
                            JSON.stringify(item)
                        );
                    }
                    return String(item);
                };

                const labelsToRender = backendLabels && backendLabels.length === ids.length
                    ? backendLabels
                    : ids.map((item: any) => {
                        const label = getLabel(item);
                        return String(label || '');
                    });

                return (
                    <div key={meta.uid} className="flex flex-wrap gap-2">
                        {labelsToRender.map((label: string, idx: number) => {
                            const stableKey = coerceUid(ids[idx]);
                            const drillUid = coerceRelationDrillInUid(ids[idx]);
                            const canDrill = Boolean(drillUid && relationSlug && onOpenRelation);
                            return (
                                <Badge
                                    key={`${meta.uid}-${stableKey || label}-${idx}`}
                                    variant="secondary"
                                    className={`text-xs text-gray-900 dark:text-gray-100 ${canDrill ? 'cursor-pointer hover:bg-secondary/80' : ''}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (canDrill) {
                                            onOpenRelation!({ targetSlug: String(relationSlug), uid: String(drillUid) });
                                        }
                                    }}
                                >
                                    {label}
                                </Badge>
                            );
                        })}
                    </div>
                );
            }
            case "systemrelation": {
                const raw = systemRelationValue;
                if (raw === null || raw === undefined || raw === "") {
                    return <span key={meta.uid}></span>;
                }
                const storedKey = coerceUid(raw);
                const drillUid = coerceRelationDrillInUid(raw);
                const labelFromBackend =
                    rowOriginal && relationLabelKey && typeof rowOriginal[relationLabelKey] === 'string'
                        ? String(rowOriginal[relationLabelKey])
                        : '';
                const cached =
                    storedKey != null && relationSlug ? relationLabelCache[relationSlug]?.get(String(storedKey)) : '';
                const rawDisplay = labelFromBackend || cached || (storedKey != null ? String(storedKey) : '');
                const display = formatDateOnlyFromIsoLike(rawDisplay) || rawDisplay;
                if (!display) return <span key={meta.uid}></span>;
                const canDrill = Boolean(drillUid && relationSlug && onOpenRelation);
                if (!canDrill) {
                    return (
                        <span key={meta.uid} className="truncate max-w-full" title={display}>
                            {display}
                        </span>
                    );
                }
                return (
                    <button
                        key={meta.uid}
                        type="button"
                        className="text-left text-gray-900 dark:text-gray-100 hover:underline truncate max-w-full"
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenRelation!({ targetSlug: String(relationSlug), uid: String(drillUid) });
                        }}
                        title={display}
                    >
                        {display}
                    </button>
                );
            }
            case "code": {
                const codeVal = value.getValue();
                const rawDisplay = codeVal != null && String(codeVal).trim() !== "" ? String(codeVal) : "";
                const display = formatDateOnlyFromIsoLike(rawDisplay) || rawDisplay;
                return <div key={meta.uid} className="font-mono text-sm truncate max-w-full" title={display}>{display || "—"}</div>;
            }
            case "gpx": {
                const raw = value.getValue();
                const label = gpxTableLabel(raw);
                const gpx = parseGpxValue(raw);
                const canOpen = Boolean(gpx && gpx.track.length > 0);
                return (
                    <div key={meta.uid} className="max-w-full">
                        <button
                            type="button"
                            className={`truncate max-w-full text-left ${canOpen ? "text-blue-700 hover:underline dark:text-blue-300" : ""} ${gpx?.isRecording ? "font-semibold text-orange-700 dark:text-orange-300" : ""}`}
                            disabled={!canOpen}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (canOpen) setGpxMapOpen(true);
                            }}
                            title={label}
                        >
                            {label}
                        </button>
                        <GpxMapModal
                            open={gpxMapOpen}
                            value={gpx}
                            title={meta.label || meta.name}
                            onClose={() => setGpxMapOpen(false)}
                        />
                    </div>
                );
            }
            default:
                return <div key={meta.uid}>Unsupported type ({JSON.stringify(_type)})</div>
        }


    }

    return <div onClick={() => { }}>{field()}</div>
}

const LabelTyped = ({ value, meta }: { value: any, meta: any }) => {
    const user = useSelector((state: any) => state.user)
    const relationSlug = meta?.value
    const getNestedValue = (obj: any, path?: string): any => {
        if (!obj || typeof obj !== "object" || !path) return undefined;
        if (!path.includes(".")) return obj[path];
        return path.split(".").reduce((acc: any, key: string) => (acc && typeof acc === "object" ? acc[key] : undefined), obj);
    };
    const systemRelationValue = meta?.sourceField
        ? getNestedValue(value, meta.sourceField)
        : (relationSlug === 'forms' ? getNestedValue(value, 'registrationFormUid') : value);
    const normalizedType =
        typeof meta?.type === "string"
            ? meta.type.trim().toLowerCase()
            : String(meta?.type ?? "").toLowerCase();
    if (isLinkFieldType(normalizedType)) {
        return (
            <LinkFieldDisplay
                key={meta.uid}
                fieldType={normalizedType}
                value={value}
                className="text-sm truncate max-w-full block"
            />
        )
    }
    switch (normalizedType) {
        case 'text': {
            const textPlain = htmlToPlainText(value);
            const textDateOnly = formatDateOnlyFromIsoLike(textPlain);
            if (textDateOnly) {
                return <span className="text-sm truncate max-w-full block" key={meta.uid} title={textDateOnly}>{textDateOnly}</span>;
            }
            return textPlain.length > 0 ? <span className="text-sm truncate max-w-full block" key={meta.uid} title={textPlain}>{textPlain}</span> : <></>;
        }
        case 'textarea': {
            const areaPlain = htmlToPlainText(value);
            const areaDateOnly = formatDateOnlyFromIsoLike(areaPlain);
            if (areaDateOnly) {
                return <span className="text-sm truncate max-w-full block" key={meta.uid} title={areaDateOnly}>{areaDateOnly}</span>;
            }
            return areaPlain.length > 0 ? <span className="text-sm truncate max-w-full block" key={meta.uid} title={areaPlain}>{areaPlain}</span> : <></>;
        }
        case 'relation':
            return (<span key={meta.uid}><RelationField
                table={meta.value}
                label={meta.label}
                fields={meta.fields}
                relationFieldKey={meta.name}
                canEdit={false}
                onChange={() => { }}
                readOnlyUids={[value]}
                allowedGroups={user.info?.groups}
                hideLabel={true}
            /></span>)
        case "richtext": {
            const richPlain = htmlToPlainText(coerceRichTextValue(value));
            return richPlain.length > 0 ? (
                <span className="text-sm truncate max-w-full block" key={meta.uid} title={richPlain}>
                    {richPlain}
                </span>
            ) : (
                <></>
            );
        }
        case "image":
            return (
                <Image
                    filename={value}
                    alt={meta.label}
                    connectorAssetPublicUrl={meta.connectorAssetPublicUrl || resolveConnectorAssetPublicUrl(meta)}
                    readOnly={Boolean(meta.connectorAssetPublicUrl || resolveConnectorAssetPublicUrl(meta))}
                    collectionSlug={meta.collectionSlug}
                    fieldName={meta.name}
                />
            )
        case "file":
            if (value && value !== '') {
                const fileUrl = resolveLabwickUploadUrl(String(value));
                const displayName = value.split('/').pop() || value;
                return (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                        {displayName}
                    </a>
                );
            }
            return <span className="text-muted-foreground">—</span>;
        case "calendar":
            return (
                <span>
                    <EditCalendar value={value} readOnly />
                </span>
            )
        case "checkbox":
            return (
                <span className={`rounded-xl w-5 h-5 -top-5 ${value === true ? "bg-green-500" : "bg-red-300"}`} />
            )
        case "datetime":
            const dateOnlyFromIso = formatDateOnlyFromIsoLike(value);
            if (dateOnlyFromIso) {
                return <span className="text-sm">{dateOnlyFromIso}</span>;
            }
            const parsedDate = parseTableTimestamp(value);
            if (parsedDate === null) {
                return <span className="text-sm"></span>;
            } else {
                const isDateOnlyString =
                    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
                if (isDateOnlyString) {
                    return <span className="text-sm">{parsedDate.toDateString()}</span>;
                }
                return <span className="text-sm">{parsedDate.toDateString()} - {parsedDate.toLocaleTimeString('en-GB')}</span>;
            }
        case "date":
        case "dateonly":
            const parsedDateOnly = parseTableTimestamp(value);
            if (parsedDateOnly === null) {
                return <span className="text-sm"></span>;
            } else {
                return <span className="text-sm">{parsedDateOnly.toDateString()}</span>;
            }
        case "timeonly":
            const parsedTimeOnly = parseTableTimestamp(value);
            if (parsedTimeOnly === null) {
                return <span className="text-sm"></span>;
            } else {
                return <span className="text-sm">{parsedTimeOnly.toLocaleTimeString('en-GB')}</span>;
            }
        case "number":
            if (value === null || value === undefined) {
                return <span className="text-sm"></span>;
            }
            return <span className="text-sm" key={meta.uid}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
        case "select":
        case "dropdown": {
            const options = parseSelectOptions(meta?.value ?? meta?.options);
            const label = getSelectLabel(value, options);
            return <span className="text-sm" key={meta.uid}>{label}</span>;
        }
        case "multiplerelation": {
            const items = Array.isArray(value) ? value : value ? [value] : [];
            if (items.length === 0) {
                return <span className="text-sm" key={meta.uid}></span>;
            }

            const getLabel = (item: any) => {
                if (item === null || item === undefined) return "";
                if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
                    const cached = relationSlug ? relationLabelCache[relationSlug]?.get(String(item)) : undefined;
                    return cached || String(item);
                }
                if (typeof item === "object") {
                    const fromFields = buildLabelFromFieldsForExport(item, meta?.fields);
                    if (fromFields) return fromFields;
                    return (
                        item.label ||
                        item.name ||
                        item.title ||
                        item.uid ||
                        item.id ||
                        JSON.stringify(item)
                    );
                }
                return String(item);
            };

            return (
                <span key={meta.uid} className="flex flex-wrap gap-2">
                    {items.map((item: any, idx: number) => {
                        const label = getLabel(item);
                        return (
                            <Badge
                                key={`${meta.uid}-${label}-${idx}`}
                                variant="secondary"
                                className="text-xs"
                            >
                                {label}
                            </Badge>
                        );
                    })}
                </span>
            );
        }
        case "systemrelation": {
            const raw = systemRelationValue;
            if (raw === null || raw === undefined || raw === "") {
                return <span className="text-sm" key={meta.uid}></span>;
            }
            const key = typeof raw === "object" ? (raw?.uid ?? raw?.id) : raw;
            const cached = relationSlug ? relationLabelCache[relationSlug]?.get(String(key)) : undefined;
            return <span className="text-sm" key={meta.uid}>{cached || String(key ?? "")}</span>;
        }
        case "code": {
            const codeDisplay = value != null && String(value).trim() !== "" ? String(value) : "";
            return <span className="text-sm font-mono" key={meta.uid}>{codeDisplay || "—"}</span>;
        }
        default:
            return <span key={meta.uid}>Unsupported type ({JSON.stringify(meta.type)})</span>

    }
}

function parseFieldKeysForExport(fields: any): string[] {
    if (!fields) return [];
    if (Array.isArray(fields)) return fields.map((f: any) => String(f).trim()).filter(Boolean);
    if (typeof fields === "string") return fields.split(",").map((f: string) => f.trim()).filter(Boolean);
    return [];
}

function getNestedValueForExport(obj: any, path: string): any {
    if (!obj || typeof obj !== "object") return undefined;
    if (!path.includes(".")) return obj[path];
    return path.split(".").reduce((acc: any, key: string) => (acc && typeof acc === "object" ? acc[key] : undefined), obj);
}

function buildLabelFromFieldsForExport(item: any, fieldsToUse: any): string {
    const keys = parseFieldKeysForExport(fieldsToUse);
    if (!keys.length) return "";
    const parts = keys
        .map((k: string) => getNestedValueForExport(item, k))
        .filter((v: any) => v !== undefined && v !== null && String(v).trim() !== "");
    return parts.join(" ").trim();
}

function stringifyFlexibleUserName(raw: unknown): string {
    if (raw == null) return "";
    if (typeof raw === "string") return raw.trim();
    if (typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        const first = o.first != null ? String(o.first).trim() : "";
        const last = o.last != null ? String(o.last).trim() : "";
        const joined = [first, last].filter(Boolean).join(" ").trim();
        if (joined) return joined;
    }
    return "";
}

function extractUserNameAndEmail(record: any): { name: string; email: string } {
    const emailRaw = record?.email ?? record?.info?.email;
    const email = emailRaw != null ? String(emailRaw).trim() : "";

    const first = record?.firstName != null ? String(record.firstName).trim() : "";
    const last = record?.lastName != null ? String(record.lastName).trim() : "";
    const fromFirstLast = [first, last].filter(Boolean).join(" ").trim();

    const displayName = record?.displayName != null ? String(record.displayName).trim() : "";
    const nameField = stringifyFlexibleUserName(record?.name);
    const infoName = record?.info?.name != null ? String(record.info.name).trim() : "";

    const name =
        fromFirstLast ||
        displayName ||
        nameField ||
        infoName ||
        "";

    return { name, email };
}

function applyMongoUserToUserCaches(record: any): void {
    const keyRaw = record?.uid ?? record?.id ?? record?._id;
    if (keyRaw == null) return;
    const keyStr = String(keyRaw).trim();
    if (!keyStr) return;
    if (!relationLabelCache["users"]) {
        relationLabelCache["users"] = new Map<string, string>();
    }
    const cache = relationLabelCache["users"];
    const { name, email } = extractUserNameAndEmail(record);
    const nameDisplay =
        name ||
        (record?.label != null ? String(record.label).trim() : "") ||
        (record?.title != null ? String(record.title).trim() : "") ||
        keyStr;
    userUidDisplayCache.set(keyStr, { name: nameDisplay, email });
    const label =
        nameDisplay && email ? `${nameDisplay} — ${email}` : nameDisplay || email || keyStr;
    cache.set(keyStr, String(label));
}

/**
 * Preloads the relation label cache for a slug by fetching all related items (paginated for API).
 * Call before export so every relation ID in the export can be resolved to a label.
 * On 404 (component not found) or 403 (permission denied), skips that slug so export still completes with IDs.
 */
export async function ensureRelationLabelsLoaded(
    relationSlug: string,
    fieldsToUse: any,
    type: string
): Promise<void> {
    if (!relationSlug) return;
    if (!relationLabelCache[relationSlug]) {
        relationLabelCache[relationSlug] = new Map<string, string>();
    }
    const cache = relationLabelCache[relationSlug];

    const setLabel = (record: any) => {
        const key = record?.uid ?? record?.id;
        if (key == null) return;
        const keyStr = String(key);
        if (relationSlug === "users") {
            applyMongoUserToUserCaches(record);
            return;
        }
        const label =
            buildLabelFromFieldsForExport(record, fieldsToUse) ||
            record?.label ||
            record?.name ||
            record?.title ||
            keyStr;
        cache.set(keyStr, String(label));
    };

    try {
        if (type === "systemrelation") {
            const systemSource = getSystemRelationSource(relationSlug);
            if (!systemSource) return;
            const raw = await systemSource.fetchItems();
            const related = Array.isArray(raw) ? raw : [];
            related.forEach(setLabel);
            return;
        }

        const slugsToTry: string[] =
            typeof relationSlug === "string" && relationSlug.includes("_")
                ? [relationSlug, relationSlug.split("_")[0]]
                : [relationSlug];
        let lastErr: unknown;
        for (const slugForApi of slugsToTry) {
            try {
                let page = 1;
                const limit = 100;
                for (; ;) {
                    const result = await fetchItemsAPI(slugForApi, { aclAction: "relate", page, limit });
                    const items = result?.items ?? [];
                    items.forEach(setLabel);
                    if (items.length < limit) break;
                    page += 1;
                }
                return;
            } catch (e) {
                lastErr = e;
                const msg = e instanceof Error ? e.message : String(e);
                const is404 = msg.includes("404") || msg.includes("Component not found");
                if (!is404 || slugForApi === slugsToTry[slugsToTry.length - 1]) break;
            }
        }
        throw lastErr;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const is404 = msg.includes("404") || msg.includes("Component not found");
        const is403 = msg.includes("403") || msg.includes("permission");
        if (is404 || is403) {
            console.warn(
                `Export: could not load relation labels for "${relationSlug}" (${is404 ? "component not found" : "permission denied"}). Relation column will show IDs.`
            );
        } else {
            throw err;
        }
    }
}

/**
 * Returns a display string for a relation field value (IDs resolved via cache or object labels).
 * Used by exports (Excel, CSV, etc.) so relation columns show labels instead of raw IDs.
 */
export function getRelationDisplayValue(meta: any, value: any): string {
    const type = meta?.type;
    if (type !== "relation" && type !== "multiplerelation" && type !== "systemrelation") {
        return value != null ? String(value) : "";
    }
    const relationSlug = meta?.value;
    const fieldsToUse = meta?.fields;

    const getLabel = (item: any): string => {
        if (item === null || item === undefined) return "";
        if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
            const cached = relationSlug ? relationLabelCache[relationSlug]?.get(String(item)) : undefined;
            return cached || String(item);
        }
        if (typeof item === "object") {
            const fromFields = buildLabelFromFieldsForExport(item, fieldsToUse);
            if (fromFields) return fromFields;
            return (
                item.label ||
                item.name ||
                item.title ||
                String(item.uid ?? item.id ?? "")
            ) || JSON.stringify(item);
        }
        return String(item);
    };

    if (value == null || value === "") return "";
    const items = Array.isArray(value) ? value : [value];
    return items.map(getLabel).filter(Boolean).join(", ");
}

/**
 * Formats relation field storage values as comma-separated uids for export re-import into Labwick.
 */
export function relationRawToExportIds(raw: unknown, fieldMeta: { type: string }): string {
    const type = fieldMeta?.type;
    if (type !== "relation" && type !== "multiplerelation" && type !== "systemrelation") {
        return raw != null ? String(raw) : "";
    }
    if (raw == null || raw === "") return "";
    if (type === "multiplerelation") {
        const arr = Array.isArray(raw) ? raw : [raw];
        const ids = arr
            .map((item) => {
                if (item == null || item === "") return "";
                if (typeof item === "object" && item !== null && "uid" in item) {
                    return String((item as { uid: unknown }).uid).trim();
                }
                return String(item).trim();
            })
            .filter(Boolean);
        return ids.join(", ");
    }
    if (typeof raw === "object" && raw !== null && "uid" in raw) {
        return String((raw as { uid: unknown }).uid).trim();
    }
    return String(raw).trim();
}

const USERS_RELATION_LABEL_META: any = {
    type: "relation",
    value: "users",
    fields: ["firstName", "lastName", "displayName", "name", "email"],
};

export function getUserUidDisplayNameAndEmail(
    uid: string | null | undefined
): { name: string; email: string } | null {
    if (uid == null || String(uid).trim() === "") return null;
    return userUidDisplayCache.get(String(uid).trim()) ?? null;
}

/**
 * Resolve a single user UID via GET /auth/user/:uid when the users collection
 * preload is unavailable (no component, or permission denied).
 */
export async function ensureUserUidCachedFromAuth(uid: string | null | undefined): Promise<void> {
    if (uid == null || String(uid).trim() === "") return;
    const keyStr = String(uid).trim();
    if (userUidDisplayCache.has(keyStr)) return;
    const pending = userUidAuthFetchPromises.get(keyStr);
    if (pending) return pending;
    const p = (async () => {
        try {
            const user = await authAPI.getUserById(keyStr);
            if (user && typeof user === "object") {
                applyMongoUserToUserCaches({ ...user, uid: user.uid ?? keyStr });
            }
        } catch {
            /* keep UID fallback in UI */
        } finally {
            userUidAuthFetchPromises.delete(keyStr);
        }
    })();
    userUidAuthFetchPromises.set(keyStr, p);
    return p;
}

export async function hydrateUserDisplayCacheFromAuthForUids(
    uids: Iterable<string>
): Promise<void> {
    const unique = [...new Set([...uids].map((u) => String(u).trim()).filter(Boolean))];
    const missing = unique.filter((u) => !userUidDisplayCache.has(u));
    await Promise.all(missing.map((u) => ensureUserUidCachedFromAuth(u)));
}

export function getUserUidDisplayLabel(uid: string | null | undefined): string {
    if (uid == null || String(uid).trim() === "") return "";
    return getRelationDisplayValue(USERS_RELATION_LABEL_META, uid);
}

export function preloadUsersRelationLabels(): Promise<void> {
    return ensureRelationLabelsLoaded(
        "users",
        ["firstName", "lastName", "displayName", "name", "email"],
        "relation"
    );
}

export { TableCellTyped, LabelTyped }